import { memo, useMemo, useCallback, useEffect, useRef } from 'react';
import { Calendar, ArrowRight, BookOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Swiper from 'swiper';
import { Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import { blogIndex } from '../../../detail/BlogDetailPage/BlogIndex';

// Sliced once at module level — blogIndex is static, never changes at runtime
const latestPosts = blogIndex.slice(0, 6);
const LOOP_ENABLED = latestPosts.length > 3; // stable boolean, computed once

// Swiper config object — created once, never recreated
const SWIPER_CONFIG = {
  modules: [Pagination, Autoplay],
  slidesPerView: 1,
  spaceBetween: 20,
  loop: LOOP_ENABLED,
  autoplay: {
    delay: 5000,
    disableOnInteraction: false,
  },
  pagination: {
    el: '.swiper-pagination',
    clickable: true,
  },
  breakpoints: {
    640: { slidesPerView: 1, spaceBetween: 20 },
    1024: { slidesPerView: 2, spaceBetween: 24 },
    1400: { slidesPerView: 3, spaceBetween: 24 },
  },
};

// BlogCard — memoised so slide items never re-render unless post changes
const BlogCard = memo(function BlogCard({
  post,
  onClick,
}: {
  post: (typeof latestPosts)[0];
  onClick: (slug: string) => void;
}) {
  const handleClick = useCallback(() => onClick(post.slug), [onClick, post.slug]);

  return (
    <article
      onClick={handleClick}
      className="group bg-gray-800/30 backdrop-blur-sm rounded-2xl overflow-hidden border border-gray-700/50 hover:border-blue-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/10 cursor-pointer h-full flex flex-col"
    >
      {/* Image */}
      <div className="relative overflow-hidden aspect-video">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/20 to-transparent" />
        <div className="absolute top-4 left-4">
          <span className="px-3 py-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-xs font-semibold rounded-full">
            {post.category}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 flex-1 flex flex-col">
        <div className="flex items-center gap-4 text-gray-300 text-sm mb-3">
          <span className="flex items-center gap-1">
            <Calendar size={14} />
            {post.date}
          </span>
        </div>
        <h3 className="text-white text-lg font-bold mb-3 group-hover:text-blue-400 transition-colors line-clamp-2">
          {post.title}
        </h3>
        <div className="flex items-center gap-2 text-blue-400 font-semibold text-sm group/link">
          Read more
          <ArrowRight size={16} className="group-hover/link:translate-x-1 transition-transform" />
        </div>
      </div>
    </article>
  );
});

// LatestBlogs — memo wraps the whole section; no props so it never re-renders from parent
const LatestBlogs = memo(function LatestBlogs() {
  const swiperRef = useRef<Swiper | null>(null);
  const navigate  = useNavigate();

  // Stable navigate callback — never recreated
  const handleBlogClick = useCallback((slug: string) => {
    navigate(`/newsroom/${slug}`);
  }, [navigate]);

  // Swiper slides — memoised; only rebuilds if latestPosts changes (it won't)
  const slides = useMemo(() => latestPosts.map(post => (
    <div key={post.id} className="swiper-slide">
      <BlogCard post={post} onClick={handleBlogClick} />
    </div>
  )), [handleBlogClick]);

  useEffect(() => {
    swiperRef.current = new Swiper('.blog-swiper', SWIPER_CONFIG);
    return () => {
      swiperRef.current?.destroy();
    };
  }, []); // no deps — config and posts are stable module-level constants

  return (
    <section className="pt-10 pb-16 md:py-20 px-4 sm:px-6 lg:px-8" id="blog" style={{ scrollMarginTop: '100px' }}>
      <div className="container">

        {/* Header */}
        <div className="mb-12">
          <div className="mb-8 md:mb-12">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2.5 px-5 py-2.5 mb-6 bg-white/5 rounded-full border border-white/10">
                <BookOpen className="w-4 h-4 text-cyan-400" />
                <span className="text-xs md:text-sm font-semibold text-gray-300 tracking-widest uppercase">
                  Insights & Updates
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight tracking-tight">
                Latest
                <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent pl-2">
                  Blogs & Newsroom
                </span>
              </h2>
              <p className="text-base md:text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed">
                Stay updated with the latest news, and technical insights from our expert team.
              </p>
            </div>
          </div>
        </div>

        {/* Swiper */}
        <div className="relative px-1">
          <div className="blog-swiper overflow-hidden">
            <div className="swiper-wrapper pb-12">
              {slides}
            </div>
            <div className="swiper-pagination" />
          </div>
        </div>

      </div>

      <style>{`
        .blog-swiper { position: relative; }
        .blog-swiper .swiper-wrapper { display: flex; align-items: stretch; }
        .blog-swiper .swiper-slide { height: auto; display: flex; }
        .blog-swiper .swiper-pagination {
          position: absolute; bottom: 0; left: 50%; transform: translateX(-50%);
          width: auto; display: flex; justify-content: center; gap: 8px;
        }
        .blog-swiper .swiper-pagination-bullet {
          width: 10px; height: 10px; background: #4B5563; opacity: 1;
          margin: 0 !important; transition: all 0.3s ease; border-radius: 50%; cursor: pointer;
        }
        .blog-swiper .swiper-pagination-bullet:hover { background: #6B7280; transform: scale(1.2); }
        .blog-swiper .swiper-pagination-bullet-active {
          background: linear-gradient(to right, #9333EA, #3B82F6); width: 32px; border-radius: 5px;
        }
      `}</style>
    </section>
  );
});

export default LatestBlogs;